/**
 * 데모 체험 시작(/demo/).
 *
 * 이메일을 보내면 데모 사이트가 체험용 매장을 만들고 인증 코드를 보낸다.
 * 코드 확인은 셀프 세팅과 같은 엔드포인트를 그대로 쓰고, 성공하면 세팅 화면으로 넘어간다.
 */

import { busy } from './busyButton';
import initCodeInput from './codeInput';
import initScrollSpyNav from './scrollSpyNav';

const postAjax = async (root, action, data) => {
  // 임시: 화면 주소에 ?mode=test 가 있으면 서버도 Turnstile 테스트 키로 검증하게 함께 보낸다
  const mode =
    new URLSearchParams(window.location.search).get('mode') === 'test' ? { mode: 'test' } : {};
  const body = new URLSearchParams({ action, nonce: root.dataset.nonce, ...mode, ...data });
  const response = await fetch(root.dataset.ajaxUrl, {
    method: 'POST',
    body,
    credentials: 'same-origin',
  });
  const json = await response.json().catch(() => null);

  if (!json || !json.success) {
    throw new Error(json?.data?.message || `요청 실패 (HTTP ${response.status})`);
  }

  return json.data;
};

const resetCaptcha = () => {
  try {
    window.turnstile?.reset();
  } catch {
    // 위젯이 없으면 무시
  }
};

const InitDemo = () => {
  const root = document.querySelector('[data-qrshop-demo]');

  if (!root) {
    return;
  }

  const form = root.querySelector('form');
  const status = root.querySelector('[data-demo-status]');
  const statusText = root.querySelector('[data-demo-status-text]');
  const emailInput = root.querySelector('[data-demo-email]');
  const consentInput = root.querySelector('[data-demo-consent]');
  // 인증코드 6칸. 값은 항상 codeInput.js 의 컨트롤러로 이어붙여 읽는다.
  const codeInput = initCodeInput(root.querySelector('[data-demo-code]'));
  const stepEmail = root.querySelector('[data-demo-step="email"]');
  const stepCode = root.querySelector('[data-demo-step="code"]');
  const sendButton = root.querySelector('[data-demo-send]');
  const verifyButton = root.querySelector('[data-demo-verify]');
  const restartButton = root.querySelector('[data-demo-restart]');

  if (!form || !emailInput || !codeInput) {
    return;
  }

  // 코드 확인에 필요한 값. 데모 사이트가 정해 준 것이라 화면에서 바꾸지 않는다.
  let account = null;
  let step = 'email';

  // 공통 메시지(notice) 상태별 수정자. info 는 수정자 없이 기본 회색이다.
  const STATUS_CLASSES = {
    success: 'notice--success',
    error: 'notice--error',
    warning: 'notice--warning',
    info: '',
  };

  root.querySelector('[data-demo-status-close]')?.addEventListener('click', () => {
    status.hidden = true;
  });

  const setStatus = (message, state) => {
    const next = undefined === STATUS_CLASSES[state] ? 'info' : state;

    statusText.textContent = message;

    status.classList.remove('notice--success', 'notice--error', 'notice--warning');

    if ('' !== STATUS_CLASSES[next]) {
      status.classList.add(STATUS_CLASSES[next]);
    }

    // 오류는 화면 낭독기가 바로 읽어 주도록 역할을 바꾼다
    status.setAttribute('role', 'error' === next ? 'alert' : 'status');
    status.setAttribute('aria-live', 'error' === next ? 'assertive' : 'polite');
    // 내용이 없으면 메시지 자체를 숨긴다
    status.hidden = '' === message;
  };

  // 좌측 섹션 메뉴 (모바일: 상단 칩) — 보이는 단계의 카드(data-nav-label)만 담는다.
  // 단계가 바뀌면 다시 채운다 (이메일 단계: 이메일 · 약관 동의 / 인증코드 단계: 인증코드)
  const navEl = root.querySelector('[data-demo-nav]');
  const refreshNav = (container) => {
    if (!navEl) {
      return;
    }

    initScrollSpyNav(
      navEl,
      [...container.querySelectorAll('[data-nav-label]')].map((section) => ({
        el: section,
        label: section.dataset.navLabel,
      }))
    );
  };

  const showStep = (next) => {
    step = next;
    const entering = next === 'code';

    stepEmail.hidden = entering;
    stepCode.hidden = !entering;
    refreshNav(entering ? stepCode : stepEmail);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (entering) {
      codeInput.clear();
      codeInput.focus();
    } else {
      resetCaptcha();
      emailInput.focus();
    }
  };

  refreshNav(stepEmail);

  restartButton?.addEventListener('click', () => {
    account = null;
    setStatus('', '');
    showStep('email');
  });

  const start = async () => {
    const email = emailInput.value.trim();

    if (!email) {
      setStatus('이메일 주소를 입력해 주세요.', 'error');
      return;
    }

    if (consentInput && !consentInput.checked) {
      setStatus('개인정보 수집 · 이용에 동의해 주세요.', 'error');
      consentInput.focus();
      return;
    }

    const release = busy(sendButton, '준비 중…');
    setStatus('체험용 매장을 준비하고 있습니다...', 'info');

    try {
      const data = await postAjax(root, 'qrshop_demo_start', {
        email,
        turnstile: document.querySelector('[name="cf-turnstile-response"]')?.value || '',
      });

      account = { username: data.username, shop_type: data.shop_type };
      showStep('code');
      setStatus(
        data.returning
          ? '이전에 만드신 체험 매장이 있습니다. 메일로 보낸 인증 코드를 입력해 주세요.'
          : data.message || '입력하신 이메일로 인증 코드를 보냈습니다.',
        'success'
      );
    } catch (error) {
      resetCaptcha();
      setStatus(error.message, 'error');
    } finally {
      release();
    }
  };

  const verify = async () => {
    // Enter 로 폼 제출과 keydown 확인이 겹쳐 다시 불릴 수 있다 — 진행 중이면 무시
    if ('true' === verifyButton.getAttribute('aria-busy')) {
      return;
    }

    const code = codeInput.value();

    if (code.length !== 6 || !account) {
      setStatus('인증 코드 6자리를 입력해 주세요.', 'error');
      return;
    }

    const release = busy(verifyButton, '확인 중…');
    setStatus('확인 중입니다...', 'info');

    try {
      await postAjax(root, 'qrshop_setup_login_verify', {
        shop_type: account.shop_type,
        username: account.username,
        code,
      });

      setStatus('준비됐습니다. 이동 중...', 'success');
      // 로그인 뒤에는 홈으로 간다 (data-setup-url). 체험 매장 편집은 헤더 "기본" 메뉴로 들어간다
      window.location.href = root.dataset.setupUrl;
    } catch (error) {
      setStatus(error.message, 'error');
      release();
      codeInput.clear();
      codeInput.focus();
    }
  };

  form.addEventListener('submit', (event) => {
    event.preventDefault();

    if (step === 'email') {
      start();
    } else {
      verify();
    }
  });
};

export default InitDemo;
